<script lang="ts">
  /**
   * The room: your board, your opponent's board (or one shared well in
   * co-op), the header that says how the match stands, and the loop that
   * drives it. What the server puts over the boards (the lobby, the pause,
   * the resume count and the match end) are the shell's `LobbyTemplate` and
   * `ServerOverlay`, composed in `screens/duel/`; what this screen shares
   * with the solo one is in `src/play/`.
   *
   * 1. **Both boards are drawn by the same renderer from the same engine.**
   *    The opponent's canvas is not a video feed or a stream of cells: it is
   *    a second `GameState` this browser simulates from the inputs the
   *    server validated and relayed (`docs/decisions/0013`).
   * 2. **Nothing on screen is guessed.** The incoming meter, the round score,
   *    the phase and the clock all come from server messages.
   * 3. **A dropped socket is not a stopped game.** The banner says how long
   *    the seat is held for while the client reconnects, and
   *    `visibilitychange` tells the server the page is going away *before* it
   *    goes, because on iOS there is no afterwards.
   * 4. **Portrait is the primary layout.** The phone gives the whole width to
   *    the board being played, the opponent shrinks to a mini-board above it,
   *    and the touch controls sit under both. From 1024px up the two boards
   *    sit side by side at equal size.
   * 5. **One bar, and the rest is behind the menu.** The shell's `GameFrame`
   *    (`docs/decisions/0054`): its top bar is the menu button, the score
   *    over how the match stands, and the variant as a chip; its strip the
   *    rope, the curses and the connection line; `above` the opponent (or,
   *    in co-op, the partner); the stage your own board; `bottom` the touch
   *    pads. From 1024 px the frame's `split`: your board the full height,
   *    the rest down the panel beside it. Settings, How to play, the room's
   *    code and the way out are in the Game menu (`nav/GameMenu.svelte`).
   *
   * ## Back, and Leave (`docs/decisions/0047`)
   *
   * The room is an overlay entry `App.svelte` pushed, so Back leaves it.
   * While a match is live for this seat (`nav/leave-question.ts#matchIsLive`)
   * the shell's `BackGuard` is stacked on that entry: Back, and
   * every way of leaving (the Game menu's, the lobby's, an overlay's), ask
   * first, in words that say what leaving costs in
   * this exact case. In the lobby, after the match, and for a spectator,
   * nothing is asked.
   *
   * Neither the Game menu nor Settings pauses a match: it would stop the
   * opponent's board too. While one is open, game keys are neither played nor
   * swallowed:
   * `input/keyboard.ts` leaves every key pressed inside a dialog alone.
   */
  import { onMount, tick, untrack } from 'svelte';

  import {
    BackGuard,
    Chip,
    ConfirmDialog,
    GameFrame,
    Group,
    Note,
    PillButton,
    Row,
    SectionLabel,
    ServerOverlay,
  } from '@artibens/app-shell';
  import type { SheetBackAttempt } from '@artibens/app-shell';

  import { TICK_MS, ticksToMs } from '@interlock/engine';
  import type { GameState, LockResult } from '@interlock/engine';
  import {
    CURSE_LOADOUT_SIZE,
    TUG_ROPE_SCALE,
    describeCurseEffect,
    describeCurseEffectDetail,
    describeDuelConfig,
    duelRuleSet,
    duelVariantName,
    isCurseLoadout,
    isStoppedPhase,
  } from '@interlock/protocol';
  import { CURSE_EFFECTS, curseActiveAt } from '@interlock/sim';
  import type { CurseEffect, CurseWindow } from '@interlock/sim';
  import { boardTheme } from '@interlock/themes';
  import type { Room } from 'colyseus.js';

  import { startAudioForGesture } from '../audio/bootstrap.js';
  import { boardDangerFraction } from '../audio/danger.js';
  import { audio } from '../audio/engine.js';
  import { KEY_HINTS } from '../copy/keys.js';
  import { rejoinRoom } from '../net/connect.js';
  import { joinLink } from '../net/share.js';
  import { DuelClient, emptyDuelView } from '../net/duel-client.js';
  import type { DuelView, RoomLike } from '../net/duel-client.js';
  import GameMenu from '../nav/GameMenu.svelte';
  import { returnKeysToBoard } from '../nav/focus-on-show.js';
  import { leaveQuestion, matchIsLive } from '../nav/leave-question.js';
  import { fitToStage } from '../play/fit-stage.js';
  import { playLockFeedback } from '../play/lock-feedback.js';
  import { PlayInput } from '../play/play-input.svelte.js';
  import { startPlayScreen } from '../play/play-screen.js';
  import { Canvas2DRenderer } from '../render/canvas2d-renderer.js';
  import { EffectsController } from '../render/effects.js';
  import { deriveClearEffectProfile } from '../render/theme-effects.js';
  import { account } from '../state/account.svelte.js';
  import { celebrate } from '../state/celebration.svelte.js';
  import { settings } from '../state/settings.svelte.js';
  import { recordVersusAiResult, recordVersusResult } from '../state/versus-stats-io.js';
  import GameGlyph from '../glyphs/GameGlyph.svelte';
  import DuelLobby from './duel/DuelLobby.svelte';
  import DuelMatchEnd from './duel/DuelMatchEnd.svelte';
  import DuelStopped from './duel/DuelStopped.svelte';
  import IncomingMeter from './IncomingMeter.svelte';
  import TouchControls from './TouchControls.svelte';

  let {
    room,
    role,
    name = '',
    onLeave,
    onSettings,
    onHowToPlay,
    overlayOpen = false,
  }: {
    room: Room;
    role: 'player' | 'spectator';
    /** The display name to rejoin under. The server re-sanitises it either way. */
    name?: string;
    onLeave: () => void;
    /** Opens Settings. */
    onSettings: () => void;
    /** Opens How to play. */
    onHowToPlay: () => void;
    /** True while a page of the app's (Settings, How to play) is open over the room. */
    overlayOpen?: boolean;
  } = $props();

  // The room is fixed for the life of this component (App.svelte keys on it),
  // so seeding the view from the props once is right — `untrack` says so
  // rather than leaving Svelte to warn about a prop read in a place that is
  // never re-run. Everything after the first frame comes from the server.
  let view: DuelView = $state(
    untrack((): DuelView => ({ ...emptyDuelView(), status: 'connecting', role })),
  );

  let ownCanvas: HTMLCanvasElement | undefined = $state();
  let opponentCanvas: HTMLCanvasElement | undefined = $state();
  let ownStage: HTMLDivElement | undefined = $state();
  let opponentStage: HTMLDivElement | undefined = $state();
  let ready = $state(false);
  /** Test-observable counter — see `GameScreen.svelte`'s identical field for why. */
  let clearFxCount = $state(0);

  const input = new PlayInput();

  const mySeat = $derived(view.seat);
  const seatStates = $derived(view.state?.seats ?? []);
  const incoming = $derived(mySeat === null ? 0 : (seatStates[mySeat]?.incoming ?? 0));
  const opponentIncoming = $derived(mySeat === null ? 0 : (seatStates[1 - mySeat]?.incoming ?? 0));
  const myLines = $derived(mySeat === null ? 0 : (seatStates[mySeat]?.lines ?? 0));
  const opponentLines = $derived(mySeat === null ? 0 : (seatStates[1 - mySeat]?.lines ?? 0));
  const scores = $derived(view.room?.players.map((player) => player.roundsWon) ?? [0, 0]);
  const myScore = $derived(mySeat === null ? (scores[0] ?? 0) : (scores[mySeat] ?? 0));
  const opponentScore = $derived(mySeat === null ? (scores[1] ?? 0) : (scores[1 - mySeat] ?? 0));
  const players = $derived(view.room?.players ?? []);
  const opponent = $derived(players.find((player) => player.seat !== mySeat) ?? null);
  /**
   * Whether there is an opponent at all.
   *
   * A seat nobody has taken is reported with an empty name and a `gone`
   * presence, which is truthful and reads terribly: without this the lobby of
   * a freshly created room announces "has left" about a person who has never
   * existed.
   */
  const hasOpponent = $derived((opponent?.name ?? '') !== '');
  const opponentName = $derived(hasOpponent ? (opponent?.name ?? '') : 'Waiting…');
  const bothPresent = $derived(players.filter((player) => player.connected).length === 2);

  /**
   * Whether the other seat is this server's own AI.
   *
   * Read from `PlayerView.bot`, never from the name. The server sets that
   * field from the seat it built itself, and `sanitiseDisplayName` would
   * happily let a person call themselves `AI — Hard` — so a screen that
   * decided this by string comparison would be trusting a player's own typing
   * about who they are.
   */
  const opponentIsBot = $derived((opponent?.bot ?? null) !== null);
  const vsAi = $derived(players.some((player) => player.bot !== null));

  /**
   * A guest's own record of this match's outcome, kept on this device. A
   * signed-in player's copy of the same match is kept by the server, so this
   * fires only for a guest, and only for a seat that played (never a
   * spectator).
   *
   * `matchRecorded` resets whenever `view.matchEnd` goes back to `null` —
   * `rematch()` reuses this same component instance for another round inside
   * the same room, and each finished round is its own match worth a row.
   */
  let matchRecorded = false;
  $effect(() => {
    const ended = view.matchEnd;
    if (ended === null) {
      untrack(() => {
        matchRecorded = false;
      });
      return;
    }
    untrack(() => {
      if (matchRecorded) return;
      matchRecorded = true;
      if (view.role !== 'player' || account.status === 'signed-in') return;
      const outcome: 'win' | 'loss' | 'draw' =
        ended.reason === 'coop-cleared'
          ? 'win'
          : ended.reason === 'coop-topped-out'
            ? 'loss'
            : ended.winner === null
              ? 'draw'
              : ended.winner === mySeat
                ? 'win'
                : 'loss';
      // Two stores, never one. A guest's local tally is the honest mirror of
      // what the server does for a signed-in player — and the server keeps
      // vs-AI results in their own rows (`VariantRecord.vsAi`), so a guest's
      // does too. Adding a bot win into the same bucket as a win over a person
      // would make this device's stats screen the one place in the app that
      // quietly conflates them.
      if (vsAi) recordVersusAiResult(rules.variant, outcome);
      else recordVersusResult(rules.variant, outcome);
    });
  });

  /** The win/lose sting — independent of `matchRecorded` above (that guard is specific to the guest-only local record; every player hears the match end). */
  let matchEndSfxPlayed = false;
  $effect(() => {
    const ended = view.matchEnd;
    if (ended === null) {
      untrack(() => {
        matchEndSfxPlayed = false;
      });
      return;
    }
    untrack(() => {
      if (matchEndSfxPlayed || view.role !== 'player') return;
      matchEndSfxPlayed = true;
      const lost =
        ended.reason === 'coop-topped-out' || (ended.winner !== null && ended.winner !== mySeat);
      audio.playSfx(lost ? 'lose' : 'win');
      // A burst for a match win, including a co-op clear; never for a draw
      // or a loss.
      if (!lost) celebrate();
    });
  });

  const graceSeconds = $derived(
    view.graceMsLeft === null ? null : Math.ceil(view.graceMsLeft / 1000),
  );
  const opponentGraceSeconds = $derived(
    view.opponentGraceMsLeft === null ? null : Math.ceil(view.opponentGraceMsLeft / 1000),
  );

  // The whole variant surface on this screen, derived in one place from the
  // rules the server sent — no component below branches on a variant id.
  const rules = $derived(view.rules);
  const ruleSet = $derived(duelRuleSet(rules));
  const variantLabel = $derived(duelVariantName(rules.variant));
  const configLabel = $derived(describeDuelConfig(rules));
  const linesTarget = $derived(ruleSet.linesTarget);
  const clockMs = $derived(view.timeLeftMs);
  /**
   * Co-op turns this screen into one board with two pieces on it.
   *
   * A separate screen was the alternative and it would have been two copies of
   * the connection banner, the room-share panel, the ready-up lobby, the touch
   * controls and the results overlay — none of which co-op changes at all. What
   * co-op changes is the *body*: one stage instead of two, a shared line count
   * instead of a scoreline, and a marker on the piece that is not yours.
   */
  const coop = $derived(ruleSet.coop);
  /** The slot the partner drives, for the renderer's "not yours" marker. */
  const partnerSlot = $derived(coop && mySeat !== null ? 1 - mySeat : null);
  const partnerName = $derived(hasOpponent ? (opponent?.name ?? '') : 'Waiting for a partner…');
  /** True while anything about the match is not "playing normally". */
  const alarmed = $derived(
    view.status === 'reconnecting' ||
      view.status === 'closed' ||
      view.presence !== 'online' ||
      (hasOpponent && view.opponentPresence !== 'online'),
  );

  /* --- leaving ---------------------------------------------------------- */

  /** True while "Leave the match?" is on screen. Written only by the guard. */
  let asking = $state(false);
  const guard = new BackGuard((next) => {
    asking = next;
  });
  onMount(() => () => {
    guard.dispose();
  });

  /** Whether leaving now would throw a match away — see `nav/leave-question.ts`. */
  const live = $derived(
    matchIsLive({
      role: view.role,
      phase: view.phase,
      matchEnded: view.matchEnd !== null,
      refused: view.status === 'refused',
    }),
  );
  $effect(() => {
    guard.setLive(live);
  });

  const question = $derived(
    leaveQuestion({
      coop,
      vsAi,
      matchType: view.matchType,
      phase: view.phase,
      otherName: hasOpponent ? (opponent?.name ?? '') : '',
    }),
  );

  let rootEl: HTMLDivElement | undefined = $state();

  /** The safe answer. The guard re-arms, and the keys go back to the board rather than to the Leave button. */
  function keepPlaying(): void {
    guard.keep();
    void tick().then(() => {
      returnKeysToBoard(rootEl);
    });
  }

  /** Every way of leaving: ask while a match is live, otherwise just go. */
  function requestLeave(): void {
    if (!guard.request()) onLeave();
  }

  /**
   * A Back the server's own overlays refused (the lobby, the pause, the
   * match end: `DuelLobby`, `DuelStopped`, `DuelMatchEnd`). It is a way of
   * leaving like any other: asked about while the match is live, otherwise
   * simply taken. Escape and a press beside them are not reported.
   */
  function onOverlayBack(reason: SheetBackAttempt): void {
    if (reason === 'back') requestLeave();
  }

  /* --- the Game menu --------------------------------------------------- */

  let menuOpen = $state(false);

  /**
   * Something the server put over the boards is up: the lobby, the pause or
   * the count back in, the match end, a lost connection. Each is a shell
   * dialog that hands focus back to whatever had it when it goes.
   */
  const serverCardUp = $derived(
    (view.phase === 'lobby' && view.role === 'player') ||
      view.matchEnd !== null ||
      view.matchStopped ||
      isStoppedPhase(view.phase) ||
      view.status === 'refused' ||
      view.status === 'closed',
  );

  /** The lobby page lies over the room: a seat for a person, or a match against the computer. */
  const lobbyCovers = $derived(
    view.phase === 'lobby' && view.role === 'player' && view.matchEnd === null,
  );

  // Whatever was open over the room has closed. The shell hands focus back to
  // whatever opened it (the menu button), and a focused button would take the
  // next Space instead of the board.
  $effect(() => {
    if (menuOpen || overlayOpen || serverCardUp) return;
    void tick().then(() => {
      returnKeysToBoard(rootEl);
    });
  });

  /** The Game menu's Share: the native sheet where there is one, the link on the clipboard where not. */
  async function shareRoom(): Promise<void> {
    const code = view.code;
    const link = joinLink(code, window.location.origin);
    try {
      if (typeof navigator.share === 'function') {
        await navigator.share({
          title: 'Interlock',
          text: `Watch my Interlock room. The code is ${code}`,
          url: link,
        });
      } else {
        await navigator.clipboard.writeText(link);
      }
    } catch {
      // Dismissed, or refused. The code is in the row the button sits on.
    }
  }

  /* --- the pause ------------------------------------------------------- */

  // The match has stopped (`DuelPause`), and then everyone is back and it
  // counts down to resuming (`DuelResume`): two moments of one thing. The
  // seats being waited for come straight off the room roster.
  const matchStopped = $derived(view.matchStopped || isStoppedPhase(view.phase));
  const resumeSeconds = $derived(
    view.resumeInMs === null ? null : Math.max(1, Math.ceil(view.resumeInMs / 1000)),
  );
  const waitingFor = $derived(
    players.filter((player) => player.name !== '' && player.presence !== 'online'),
  );

  /**
   * Countdown ticks, for the round-start count and the resume count alike:
   * both count a whole second at a time. A tick plays on every distinct
   * second shown, and a brighter "go" the instant a countdown that actually
   * ran disappears (never on the first render, when nothing was counting).
   */
  const roundCountdownSeconds = $derived(
    view.countingDown ? Math.max(1, Math.ceil(view.countdownMs / 1000)) : null,
  );
  let lastCountdownSeconds: number | null = null;
  $effect(() => {
    const seconds = roundCountdownSeconds ?? resumeSeconds;
    untrack(() => {
      if (seconds !== null && seconds !== lastCountdownSeconds) audio.playSfx('countdownTick');
      else if (seconds === null && lastCountdownSeconds !== null) audio.playSfx('countdownGo');
      lastCountdownSeconds = seconds;
    });
  });

  /** "You" for your own seat: a pause overlay that says your name at you reads as a bug. */
  function whoLabel(seat: number): string {
    if (seat === mySeat) return 'You';
    return players[seat]?.name ?? 'They';
  }

  function waitingLine(player: (typeof players)[number]): string {
    const who = whoLabel(player.seat);
    const isMe = player.seat === mySeat;
    if (player.presence === 'paused') {
      const left = player.pauseMsLeft;
      const budget = left === null ? '' : ` · ${String(Math.ceil(left / 1000))}s of pause left`;
      return isMe
        ? `Your app is in the background${budget}`
        : `${who} is in the background${budget}`;
    }
    if (player.presence === 'reconnecting') {
      const left = player.graceMsLeft;
      const held = left === null ? '' : ` · seat held ${String(Math.ceil(left / 1000))}s`;
      return isMe ? `You lost your connection${held}` : `${who} lost their connection${held}`;
    }
    return isMe ? 'You have left' : `${who} has left`;
  }

  const waitingLines = $derived(waitingFor.map(waitingLine));
  /** This client's own socket is down or coming back, so a rejoin is worth offering. */
  const canRejoin = $derived(view.status === 'closed' || view.status === 'reconnecting');

  /* --- the end ---------------------------------------------------------- */

  const matchEndTitle = $derived.by(() => {
    const ended = view.matchEnd;
    if (ended === null) return '';
    if (ended.reason === 'coop-cleared') return 'You cleared it, together';
    if (ended.reason === 'coop-topped-out') return 'The well filled up';
    if (ended.winner === null) return coop ? 'Run ended' : 'Match drawn';
    if (ended.winner === mySeat) return 'You win the match';
    return `${opponentName} wins the match`;
  });
  const matchEndScore = $derived.by(() => {
    const ended = view.matchEnd;
    if (ended === null) return '';
    if (coop) {
      return `${String(myLines)}${linesTarget === null ? '' : `/${String(linesTarget)}`} lines`;
    }
    return `${String(ended.scores[0])} – ${String(ended.scores[1])}`;
  });

  /** The seats' final scores, side by side, your own first; one tile for the team in co-op. */
  const matchEndTiles = $derived.by(() => {
    const ended = view.matchEnd;
    if (ended === null) return [];
    if (coop) {
      return [
        {
          name: 'Together',
          score: `${String(myLines)}${linesTarget === null ? '' : `/${String(linesTarget)}`}`,
          mine: true,
        },
      ];
    }
    const own = mySeat ?? 0;
    return [
      {
        name: mySeat === null ? (players[0]?.name ?? 'Seat 1') : 'You',
        score: String(ended.scores[own] ?? 0),
        mine: mySeat !== null,
      },
      {
        name: players[1 - own]?.name || 'Opponent',
        score: String(ended.scores[1 - own] ?? 0),
        mine: false,
      },
    ];
  });

  /* --- curses ---------------------------------------------------------- */

  const isCurses = $derived(ruleSet.curses);
  /** This seat's own picked loadout, from the roster — `null` before it has picked one. */
  const myLoadout = $derived(mySeat === null ? null : (players[mySeat]?.curseLoadout ?? null));
  /**
   * The loadout picker's own working selection, seeded from whatever this
   * seat already picked (a rejoin, or a page reload mid-lobby) and otherwise
   * empty — `readyUp` is disabled until exactly `CURSE_LOADOUT_SIZE` are
   * chosen, so there is no need to invent a default here the way the server
   * does for a seat that never picks at all.
   */
  let loadoutChoice: CurseEffect[] = $state([]);
  $effect(() => {
    const picked = myLoadout;
    untrack(() => {
      if (picked !== null && loadoutChoice.length === 0) loadoutChoice = [...picked];
    });
  });
  const loadoutComplete = $derived(isCurseLoadout(loadoutChoice, CURSE_LOADOUT_SIZE));

  function toggleCurse(effect: CurseEffect): void {
    if (loadoutChoice.includes(effect)) {
      loadoutChoice = loadoutChoice.filter((e) => e !== effect);
      return;
    }
    if (loadoutChoice.length >= CURSE_LOADOUT_SIZE) return;
    loadoutChoice = [...loadoutChoice, effect];
    if (loadoutChoice.length === CURSE_LOADOUT_SIZE) duelClient?.setLoadout(loadoutChoice);
  }

  /**
   * Countdown chips for this seat's own active curses — updated from the
   * render loop at a throttled rate (`docs/KNOWN-BUGS.md` item 5: DOM writes
   * next to a canvas cost paint for nothing done every frame), never faster
   * than the numbers actually change.
   */
  let myCurseChips: { effect: CurseEffect; msLeft: number }[] = $state([]);
  let opponentCurseChips: { effect: CurseEffect; msLeft: number }[] = $state([]);

  function activeCurseChips(
    windows: readonly CurseWindow[] | undefined,
    tick: number,
  ): { effect: CurseEffect; msLeft: number }[] {
    if (windows === undefined) return [];
    return windows
      .filter((w) => w.startTick <= tick && tick < w.endTick)
      .map((w) => ({ effect: w.effect, msLeft: ticksToMs(w.endTick - tick) }));
  }

  /* --- tug --------------------------------------------------------------- */

  const isTug = $derived(ruleSet.tug);
  const ropeValue = $derived(view.rope ?? 0);
  /** -1 (all the way to me) .. 1 (all the way to the opponent), for the meter's fill. */
  const ropeFraction = $derived(
    mySeat === null
      ? 0
      : Math.max(-1, Math.min(1, (mySeat === 0 ? -1 : 1) * (ropeValue / TUG_ROPE_SCALE))),
  );

  /* --- handicap ------------------------------------------------------------ */

  const myHandicapped = $derived(mySeat !== null && (players[mySeat]?.handicapped ?? false));
  const opponentHandicapped = $derived(opponent?.handicapped ?? false);

  function clock(ms: number): string {
    const total = Math.max(0, Math.ceil(ms / 1000));
    return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
  }

  onMount(() => {
    let ownState: GameState | null = null;
    let opponentState: GameState | null = null;

    // The live socket. Replaced on every successful rejoin, which is why the
    // reconnect callback below reads it rather than closing over `room`.
    let currentRoom: Room = room;

    // Two separate controllers, not one shared between both boards: a
    // clear-burst particle or ring is positioned in board cell units, so a
    // single shared snapshot handed to both `render()` calls would paint
    // your own clear's particles onto the opponent's differently-sized board
    // at the same raw coordinates. Screen shake and the correction cue stay
    // on `ownEffects` only — both are already about your own board alone.
    const ownEffects = new EffectsController();
    const opponentEffects = new EffectsController();
    /** Locks from this tick's own-board step, reported synchronously by `DuelClient.tick` via `onLocks` — see that callback's own doc comment. */
    let lockedThisTick: readonly LockResult[] = [];

    const client = new DuelClient({
      onView: (next) => {
        view = next;
      },
      onBoards: (own, opponent) => {
        ownState = own;
        opponentState = opponent;
        resize();
      },
      // `docs/prompts/06-coop.md`: "Reconciliation never teleports a piece more
      // than one cell without a visible correction cue." The client decides
      // when a jump was big enough to owe an explanation; this is where the
      // explanation is put on screen.
      onCorrection: (cells) => {
        ownEffects.triggerCorrection(cells);
      },
      onLocks: (locks) => {
        lockedThisTick = locks;
      },
      // Supplying this is what turns resilience on (`duel-client.ts`). The two
      // tokens are read at call time rather than captured, because the room
      // object changes on every successful rejoin and the session token is
      // minted by the first `welcome`.
      reconnect: async (): Promise<RoomLike> =>
        rejoinRoom({
          roomId: room.roomId,
          code: client.snapshot.code,
          name,
          role,
          reconnectionToken: currentRoom.reconnectionToken,
          sessionToken: client.token,
        }).then((joined) => {
          currentRoom = joined;
          return joined;
        }),
    });

    const ownRenderer = ownCanvas ? new Canvas2DRenderer(ownCanvas) : null;
    const opponentRenderer = opponentCanvas ? new Canvas2DRenderer(opponentCanvas) : null;

    // A spectator has no board to drive, so no input session.
    const session = role === 'player' ? input.start() : null;

    client.attach(room, role);

    function resize(): void {
      const pairs: [HTMLDivElement | undefined, Canvas2DRenderer | null, GameState | null][] = [
        [ownStage, ownRenderer, ownState],
        [opponentStage, opponentRenderer, opponentState],
      ];
      for (const [stage, renderer, state] of pairs) {
        if (!stage || renderer === null || state === null) continue;
        fitToStage(stage, renderer, state.board);
      }
    }

    // The duel's own fixed-timestep loop. The same accumulator as the solo
    // `GameLoop` — whole 1/60s ticks drained from real elapsed time — but it
    // drives `DuelClient.tick`, which is what decides when a batch of inputs
    // and a hash go out.
    let raf = 0;
    let lastFrameMs: number | null = null;
    let accumulator = 0;
    let lastChipUpdateMs = 0;
    let previousOpponentLines = 0;
    let previousIncoming = 0;
    let previousOwnActiveCurses = new Set<CurseEffect>();
    let previousOpponentActiveCurses = new Set<CurseEffect>();

    function frame(now: number): void {
      const last = lastFrameMs ?? now;
      lastFrameMs = now;
      accumulator += Math.min(250, Math.max(0, now - last));

      while (accumulator >= TICK_MS) {
        accumulator -= TICK_MS;
        const actions = session ? session.collectActions() : [];
        lockedThisTick = [];
        client.tick(actions);

        const board = client.own?.state.board;
        const theme = boardTheme(settings.appearance);
        if (board !== undefined) {
          clearFxCount += playLockFeedback({
            locks: lockedThisTick,
            actions,
            effects: ownEffects,
            boardWidth: board.width,
          });
          audio.setDanger(boardDangerFraction(board));
        }

        // The opponent's board is relayed/synced rather than stepped locally
        // (`DuelClient.onLocks`' own doc comment), so there is no `LockResult`
        // for it — a line-count delta is the best approximation available,
        // painted with a generic profile since the cleared piece's own kind
        // is not known here.
        const opponentLines = client.opponent?.state.lines ?? 0;
        const opponentBoard = client.opponent?.state.board;
        if (opponentLines > previousOpponentLines && opponentBoard !== undefined) {
          const delta = opponentLines - previousOpponentLines;
          const profile = deriveClearEffectProfile(theme.tokens);
          const topRow = opponentBoard.hiddenRows + Math.max(0, opponentBoard.visibleRows - 1);
          const rows = Array.from({ length: Math.min(delta, 4) }, (_, i) => topRow - i);
          opponentEffects.triggerClear(rows, 'I', opponentBoard.width, profile, {
            quad: delta >= 4,
          });
        }
        previousOpponentLines = opponentLines;

        ownEffects.advance(TICK_MS);
        opponentEffects.advance(TICK_MS);
      }

      // Garbage incoming — a warning blip whenever the meter grows, own seat only.
      const myIncoming = mySeat === null ? 0 : (view.state?.seats[mySeat]?.incoming ?? 0);
      if (myIncoming > previousIncoming) audio.playSfx('garbageWarning');
      previousIncoming = myIncoming;

      const theme = boardTheme(settings.appearance);
      const ownSnapshot = ownEffects.snapshot();
      const opponentSnapshot = opponentEffects.snapshot();

      // FOG and BLIND are presentation-only (`RenderFrame.fog`/`.blindNext`'s
      // own comments): read straight off each driver's own `scheduledCurses`
      // at its own current tick, the same server-announced windows RUSH and
      // MIRROR are keyed by inside the sim. No message of their own — the
      // renderer just asks the board it is about to draw.
      const ownWindows = client.own?.scheduledCurses;
      const ownTick = client.own?.tick ?? 0;
      const ownFog = isCurses && curseActiveAt(ownWindows ?? [], 'fog', ownTick);
      const ownBlind = isCurses && curseActiveAt(ownWindows ?? [], 'blind', ownTick);
      const opponentWindows = client.opponent?.scheduledCurses;
      const opponentTick = client.opponent?.tick ?? 0;

      // Curse fired/hit — a distinct cue for a curse activating on your own
      // board versus one you just fired at the opponent's. Compared against
      // last frame's active set so this fires once, on the transition, not
      // every frame the window stays open.
      if (isCurses) {
        const ownActiveNow = new Set(
          CURSE_EFFECTS.filter((effect) => curseActiveAt(ownWindows ?? [], effect, ownTick)),
        );
        for (const effect of ownActiveNow) {
          if (!previousOwnActiveCurses.has(effect)) audio.playSfx('curseHit');
        }
        previousOwnActiveCurses = ownActiveNow;

        const opponentActiveNow = new Set(
          CURSE_EFFECTS.filter((effect) =>
            curseActiveAt(opponentWindows ?? [], effect, opponentTick),
          ),
        );
        for (const effect of opponentActiveNow) {
          if (!previousOpponentActiveCurses.has(effect)) audio.playSfx('curseFired');
        }
        previousOpponentActiveCurses = opponentActiveNow;
      }

      if (ownRenderer !== null && client.own !== null) {
        ownRenderer.render({
          state: client.own.state,
          theme,
          effects: ownSnapshot,
          showGhost: settings.ghost,
          highContrast: settings.highContrast,
          // In a shared well the slot is the seat: your ghost, your hold and
          // your next queue are the ones on your side of the board, and the
          // other piece is drawn with the "not yours" marker over it.
          slot: coop ? (view.seat ?? 0) : 0,
          ...(partnerSlot === null ? {} : { partnerSlot }),
          fog: ownFog,
          blindNext: ownBlind,
        });
      }
      if (opponentRenderer !== null && client.opponent !== null) {
        opponentRenderer.render({
          state: client.opponent.state,
          theme,
          effects: opponentSnapshot,
          // The opponent's ghost is theirs to see, not yours.
          showGhost: false,
          highContrast: settings.highContrast,
          fog: isCurses && curseActiveAt(opponentWindows ?? [], 'fog', opponentTick),
          blindNext: isCurses && curseActiveAt(opponentWindows ?? [], 'blind', opponentTick),
        });
      }

      // Countdown chips: DOM text, throttled well below 60Hz —
      // `docs/KNOWN-BUGS.md` item 5. Roughly 10Hz, alongside the HUD's own
      // budget.
      if (now - lastChipUpdateMs >= 100) {
        lastChipUpdateMs = now;
        myCurseChips = activeCurseChips(ownWindows, ownTick);
        opponentCurseChips = activeCurseChips(
          client.opponent?.scheduledCurses,
          client.opponent?.tick ?? 0,
        );
      }

      raf = requestAnimationFrame(frame);
    }

    const observer = new ResizeObserver(resize);
    if (ownStage) observer.observe(ownStage);
    if (opponentStage) observer.observe(opponentStage);
    resize();
    // The server is told the page is hidden *now*, on the event itself: iOS
    // freezes a hidden page within seconds and the socket goes with it, and
    // what the server would otherwise see is a player who walked out
    // mid-round.
    const playScreen = startPlayScreen((hidden) => {
      client.setPaused(hidden);
    });
    // Menus never start the music; a room does, for as long as this screen —
    // player or spectator — is mounted (`docs/decisions/0042`).
    audio.startMusic();
    raf = requestAnimationFrame(frame);

    duelClient = client;

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      input.stop();
      playScreen.destroy();
      void client.leave();
      duelClient = null;
    };
  });

  let duelClient: DuelClient | null = null;

  function toggleReady(): void {
    // A gesture for the audio context, for the one way into a room that has
    // none: a join link that joins by itself. Starting it twice is harmless.
    startAudioForGesture();
    ready = !ready;
    duelClient?.setReady(ready);
  }

  /**
   * Vs the AI, there is nothing to ready up *for* — so don't ask.
   *
   * Playing the computer is one tap, and that tap already happened on the
   * Versus tab. The computer's seat is ready from the moment the room was
   * created, so this is the only remaining signal, and sending it
   * automatically is what turns a two-person handshake back into a
   * single-player start.
   *
   * The one thing that is still waited for is a `curses` loadout: picking
   * three of the four is a real decision this player has to make, and starting
   * the match out from under them would replace ready-up friction with
   * something worse. `toggleCurse` sends the loadout the instant it is
   * complete, and this fires on the very next flush.
   *
   * Guarded by `ready` rather than by a separate flag: after a rematch the
   * phase returns to `lobby` only if somebody left, and in that case readying
   * again is exactly right.
   */
  $effect(() => {
    const shouldStart = vsAi && view.phase === 'lobby' && view.role === 'player';
    const waitingOnALoadout = isCurses && !loadoutComplete;
    untrack(() => {
      if (!shouldStart || waitingOnALoadout || ready || duelClient === null) return;
      ready = true;
      duelClient.setReady(true);
    });
  });

  function rematch(): void {
    duelClient?.requestRematch();
  }

  function retryNow(): void {
    duelClient?.tryReconnect();
  }
</script>

{#snippet hud()}
  {#if coop}
    <span class="score" aria-label="Team progress" data-testid="duel-coop-score">
      <span data-testid="duel-score-mine">{myLines}</span>{#if linesTarget !== null}<span
          class="score-sep"
        >
          /
        </span>{linesTarget}{/if}
    </span>
  {:else}
    <span class="score" aria-label="Round score">
      <span class="score-num" data-testid="duel-score-mine"
        >{myScore}{#if myHandicapped}<span
            class="handicap-mark"
            role="img"
            aria-label="Handicap"
            title="Your gravity and incoming attack are eased this round"
            ><GameGlyph name="diamond" size={9} /></span
          >{/if}</span
      >
      <span class="score-sep">–</span>
      <span class="score-num score-num--theirs" data-testid="duel-score-theirs"
        >{opponentScore}{#if opponentHandicapped}<span
            class="handicap-mark"
            role="img"
            aria-label="Handicap"
            title="{opponentName}'s gravity and incoming attack are eased this round"
            ><GameGlyph name="diamond" size={9} /></span
          >{/if}</span
      >
    </span>
  {/if}
  <span class="sub">
    <span class="dot" class:off={alarmed}></span>
    <span class="phase" data-status={view.status} data-testid="duel-phase">
      {#if view.status === 'refused'}
        {view.statusDetail}
      {:else if view.status === 'reconnecting'}
        Reconnecting{graceSeconds === null ? '' : ` ${String(graceSeconds)}s`}
      {:else if view.status === 'closed'}
        Offline
      {:else if view.countingDown}
        Round {view.round} in {Math.ceil(view.countdownMs / 1000)}…
      {:else if view.phase === 'resuming'}
        Resuming in {resumeSeconds ?? 0}…
      {:else if view.phase === 'paused'}
        Paused
      {:else if view.phase === 'frozen'}
        Held
      {:else if view.phase === 'playing'}
        Round {view.round}
      {:else if view.phase === 'lobby'}
        {bothPresent ? 'Both here' : 'Waiting'}
      {:else if view.phase === 'roundOver'}
        Round over
      {:else}
        Match over
      {/if}
    </span>
    <span class="best-of">· {coop ? 'together' : `best of ${String(view.room?.bestOf ?? 3)}`}</span>
  </span>
{/snippet}

<!-- The variant is the room's name, its one `<h1>`, except while the lobby
     (a page with a heading of its own) covers the room. -->
{#snippet variantChip()}
  <span class="variant-block">
    <svelte:element this={lobbyCovers ? 'p' : 'h1'} class="variant"
      ><Chip tone="fill" testid="duel-variant-name">{variantLabel}</Chip></svelte:element
    >
    <span class="config" data-testid="duel-config">{configLabel}</span>
  </span>
{/snippet}

<!-- The bar's second row: what has to be seen at a glance mid-round besides
     the score. The rope in tug, each running curse with the time it has left,
     and the connection line while it has something to say. -->
{#snippet strip()}
  {#if isTug}
    <!-- One bar, one fill: the rope is a single shared value, and two bars
         would imply two numbers where there is one. -->
    <div class="rope" aria-label="Rope position" data-testid="duel-rope">
      <span class="rope-end mine">You</span>
      <div class="rope-track" data-testid="duel-rope-track">
        <div class="rope-mid"></div>
        <div
          class="rope-fill"
          class:toward-me={ropeFraction < 0}
          style="width: {Math.abs(ropeFraction) * 50}%; {ropeFraction < 0
            ? 'right: 50%;'
            : 'left: 50%;'}"
        ></div>
      </div>
      <span class="rope-end">{opponentName}</span>
    </div>
  {/if}

  {#if isCurses && (myCurseChips.length > 0 || opponentCurseChips.length > 0)}
    <div class="curses" aria-label="Active curses">
      {#each myCurseChips as chip (chip.effect)}
        <Chip tone="accent" title={describeCurseEffectDetail(chip.effect)}>
          {describeCurseEffect(chip.effect)} · {Math.ceil(chip.msLeft / 1000)}s
        </Chip>
      {/each}
      {#each opponentCurseChips as chip (chip.effect)}
        <Chip tone="outline" title={describeCurseEffectDetail(chip.effect)}>
          {opponentName}: {describeCurseEffect(chip.effect)} · {Math.ceil(chip.msLeft / 1000)}s
        </Chip>
      {/each}
    </div>
  {/if}

  <!-- The connection line, for both seats, and only while it has something
       to say: the player who never went anywhere has as much reason to know
       why their opponent stopped moving as the one who did. A sentence, in
       the danger colour only when the connection is gone. -->
  {#if alarmed}
    <div class="net" data-net={view.status === 'reconnecting' ? 'reconnecting' : view.presence}>
      <Note size="sm" centered status tone={view.status === 'closed' ? 'error' : 'muted'}>
        {#if view.status === 'reconnecting'}
          Reconnecting{graceSeconds === null
            ? ''
            : `, ${String(graceSeconds)}s left`}{view.reconnectAttempt > 0
            ? ` (try ${String(view.reconnectAttempt)})`
            : ''}.
        {:else if view.status === 'closed'}
          {view.statusDetail || 'Not connected'}.
        {:else if view.presence === 'paused'}
          Paused. The server knows.
        {/if}
        {#if !hasOpponent}
          <!-- Nobody has taken the other seat; there is nothing to report about them. -->
        {:else if view.opponentPresence === 'reconnecting'}
          {opponentName} is reconnecting{opponentGraceSeconds === null
            ? ''
            : `, ${String(opponentGraceSeconds)}s`}.
        {:else if view.opponentPresence === 'paused'}
          {opponentName} is paused.
        {:else if view.opponentPresence === 'gone'}
          {opponentName} has left.
        {/if}
      </Note>
      {#if view.status === 'closed' && view.phase !== 'matchOver'}
        <PillButton variant="plain" size="sm" label="Try again" onclick={retryNow} />
      {/if}
    </div>
  {/if}
{/snippet}

<!-- Between the bar and your board: the other side of the match. -->
{#snippet other()}
  {#if coop}
    <!-- One well, so there is no second board and no incoming meter: co-op
         has no attack. The strip carries what is not on the board: who your
         partner is, whether they are still there, and how far the two of you
         have got. -->
    <aside class="side coop-side" data-testid="duel-coop-side">
      <dl class="stats">
        <div class="stat">
          <dt>Lines</dt>
          <dd data-testid="duel-coop-lines">
            {linesTarget === null ? myLines : `${String(myLines)}/${String(linesTarget)}`}
          </dd>
        </div>
        <div class="stat">
          <dt>Partner</dt>
          <dd class:stat-away={hasOpponent && view.opponentPresence !== 'online'}>
            {partnerName}
          </dd>
        </div>
      </dl>
      <div class="coop-hint"><Note size="sm">The hatched piece is theirs.</Note></div>
    </aside>
  {:else}
    <aside class="side">
      <section class="mini">
        <h2 class="who" class:away={hasOpponent && view.opponentPresence !== 'online'}>
          <span data-testid="opponent-name">{opponentName}</span>
          {#if opponentIsBot}
            <!-- The name already says so; this says the *server* says so. -->
            <Chip title="This seat is played by the computer" testid="opponent-bot-mark"
              >Computer</Chip
            >
          {/if}
        </h2>
        <div class="mini-row">
          <div class="stage mini-stage" bind:this={opponentStage}>
            <canvas bind:this={opponentCanvas} data-testid="duel-opponent-canvas"></canvas>
          </div>
          <div class="meter-slot mini-meter"><IncomingMeter lines={opponentIncoming} /></div>
        </div>
      </section>

      <!-- The variant's own readout, derived from `DuelRuleSet`: a lines
           target and a clock appear exactly when the room's rules say they
           exist, and no markup below asks which variant is running. -->
      <dl class="stats">
        <div class="stat">
          <dt>Lines</dt>
          <dd>{linesTarget === null ? myLines : `${String(myLines)}/${String(linesTarget)}`}</dd>
        </div>
        <div class="stat stat--quiet">
          <dt>{linesTarget === null ? 'Theirs' : opponentName}</dt>
          <dd>
            {linesTarget === null
              ? opponentLines
              : `${String(opponentLines)}/${String(linesTarget)}`}
          </dd>
        </div>
        {#if clockMs !== null}
          <div class="stat">
            <dt>Left</dt>
            <dd class:stat-urgent={clockMs <= 10_000}>{clock(clockMs)}</dd>
          </div>
        {/if}
      </dl>
    </aside>
  {/if}
{/snippet}

{#snippet pads()}
  {#if view.role === 'player'}
    <TouchControls bind:root={input.touchRoot} />
  {/if}
{/snippet}

<!-- Beside the board from 1024 px (the frame draws `side` only there): the
     keys, and how often this client has been put back in step. -->
{#snippet panel()}
  {#if view.role === 'player'}
    <SectionLabel variant="sheet" as="h2">Keys</SectionLabel>
    <Group name="Keys">
      {#each KEY_HINTS as row (row.action)}
        <Row label={row.action} value={row.keys} />
      {/each}
    </Group>
  {/if}
  {#if view.resyncs > 0}
    <p class="foot"><Chip tone="outline">Back in step ×{view.resyncs}</Chip></p>
  {/if}
{/snippet}

<div class="duel" data-live={live} data-testid="duel-screen" bind:this={rootEl}>
  <!-- Under the lobby the room is out of reach: nothing behind the page it
       lays over can be tabbed to or read out. -->
  <div class="room" inert={lobbyCovers}>
    <GameFrame
      onmenu={() => {
        menuOpen = true;
      }}
      {hud}
      trailing={variantChip}
      {strip}
      above={other}
      bottom={pads}
      side={panel}
      wide="split"
      testids={{ menu: 'game-menu-open' }}
    >
      <!-- Your board, and the meter that says what is about to land on it. -->
      <section class="mine" class:mine--coop={coop}>
        {#if !coop}
          <div class="meter-slot"><IncomingMeter lines={incoming} /></div>
        {/if}
        <div class="stage" bind:this={ownStage} data-clear-fx={clearFxCount}>
          <canvas bind:this={ownCanvas} data-testid="duel-own-canvas"></canvas>
        </div>
      </section>
    </GameFrame>
  </div>

  {#if lobbyCovers}
    <!-- The lobby covers the room: a page of its own over the boards, which
         stay mounted under it (the loop draws into them from the start). -->
    <div class="lobby-layer">
      <DuelLobby
        code={view.code}
        variant={rules.variant}
        {variantLabel}
        {configLabel}
        seats={players}
        {mySeat}
        {vsAi}
        handicapEnabled={view.room?.handicapEnabled ?? false}
        curses={isCurses}
        loadout={loadoutChoice}
        loadoutSent={myLoadout !== null}
        {loadoutComplete}
        {ready}
        onToggleCurse={toggleCurse}
        onToggleReady={toggleReady}
        onLeave={requestLeave}
        onBackAttempt={onOverlayBack}
      />
    </div>
  {/if}
</div>

{#if view.matchEnd}
  <DuelMatchEnd
    eyebrow={`${variantLabel} · ${configLabel}`}
    title={matchEndTitle}
    tiles={matchEndTiles}
    score={matchEndScore}
    canRematch={view.role === 'player'}
    onRematch={rematch}
    onLeave={requestLeave}
    onBackAttempt={onOverlayBack}
  />
{:else if view.phase === 'lobby' && view.role === 'player'}
  <!-- The lobby is the page above, over the room. -->
{:else if matchStopped}
  <DuelStopped
    phase={view.phase}
    waiting={waitingLines}
    seconds={resumeSeconds}
    {canRejoin}
    onRejoin={retryNow}
    onLeave={requestLeave}
    onBackAttempt={onOverlayBack}
  />
{:else if view.status === 'refused' || view.status === 'closed'}
  <ServerOverlay
    open
    title="Disconnected"
    detail={view.statusDetail || 'The room closed the connection.'}
    primary={{ label: 'Try to rejoin', onclick: retryNow, testid: 'duel-rejoin' }}
    secondary={{
      label: 'Back to Together',
      onclick: requestLeave,
      testid: 'duel-disconnected-leave',
    }}
    onbackattempt={onOverlayBack}
    testid="duel-disconnected"
    testids={{ title: 'duel-disconnected-title' }}
  />
{/if}

<GameMenu
  open={menuOpen}
  onclose={() => {
    menuOpen = false;
  }}
  {onHowToPlay}
  {onSettings}
  room={view.code === '' || vsAi ? undefined : { code: view.code, onShare: () => void shareRoom() }}
  exitTitle="Leave room"
  exitDetail={live ? 'The match is still on. You are asked first.' : 'Back to the Together tab'}
  exitTestid="duel-leave"
  onExit={requestLeave}
/>

<!--
  The one question on this screen a player can dismiss, so the shell's
  `ConfirmDialog`: Escape, Back and the backdrop are the safe answer, and
  focus starts on it. The guard withdraws the question by itself if the
  server ends the match while it is up.
-->
<ConfirmDialog
  open={asking}
  title={question.title}
  body={question.body}
  confirmLabel={question.confirmLabel}
  cancelLabel={question.cancelLabel}
  tone="danger"
  testid="leave-confirm"
  onconfirm={onLeave}
  oncancel={keepPlaying}
/>

<style>
  .duel {
    position: relative;
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .room {
    flex: 1;
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  /* --- the bar ------------------------------------------------------- */

  /* The score: your own in ink, theirs a step quieter. */
  .score {
    font: var(--ui-text-heading);
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink);
    white-space: nowrap;
  }

  .score-num--theirs,
  .score-sep {
    color: var(--ui-ink-2);
  }

  .handicap-mark {
    display: inline-block;
    margin-left: 0.15rem;
    color: var(--ui-accent);
    vertical-align: super;
  }

  .sub {
    display: inline-flex;
    align-items: center;
    gap: var(--ui-space-1);
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  .phase[data-status='refused'],
  .phase[data-status='closed'] {
    color: var(--ui-danger);
  }

  .best-of {
    white-space: nowrap;
  }

  .dot {
    flex: 0 0 auto;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: var(--ui-presence-online);
  }

  .dot.off {
    background: var(--ui-gold);
  }

  /* The variant is the screen's heading, drawn as the bar's chip, with the
     configuration under it. */
  .variant-block {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.1rem;
    max-width: 9rem;
    min-width: 0;
  }

  .variant {
    margin: 0;
    max-width: 100%;
    display: flex;
    font: var(--ui-text-label);
    line-height: 1;
  }

  .config {
    max-width: 100%;
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    color: var(--ui-ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* --- tug's rope ---------------------------------------------------- */

  .rope {
    display: flex;
    align-items: center;
    gap: var(--ui-space-2);
    margin: var(--ui-space-1) var(--ui-space-2) 0;
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    color: var(--ui-ink-2);
  }

  .rope-end {
    flex: 0 0 auto;
    max-width: 5rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .rope-end.mine {
    color: var(--ui-ink);
    font-weight: 800;
  }

  .rope-track {
    position: relative;
    flex: 1 1 auto;
    height: 0.6rem;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-surface-2);
    overflow: hidden;
  }

  .rope-mid {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    background: var(--ui-muted);
  }

  .rope-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    background: var(--ui-accent);
  }

  .rope-fill.toward-me {
    background: var(--ui-good);
  }

  /* --- curses, and the connection line -------------------------------- */

  .curses {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ui-space-1);
    margin: var(--ui-space-1) var(--ui-space-2) 0;
  }

  .net {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: var(--ui-space-1) var(--ui-space-2) 0;
  }

  /* --- the other side, between the bar and your board ----------------- */

  .side {
    min-width: 0;
    min-height: 0;
    display: flex;
    align-items: stretch;
    gap: var(--ui-space-3);
    padding: 0 var(--ui-space-2) var(--ui-space-1);
    /*
      Sized to what the renderer needs, not to what looks small.
      `render/resize.ts` floors a cell at 8 CSS pixels, so a twenty-row board
      cannot be drawn in less than 160px of height or (with the five-column
      hold/next rail) 120px of width: ask for less and the canvas is drawn at
      the floor anyway and spills over whatever is underneath it. 11rem leaves
      exactly that once the name line is taken off.

      It costs your own board nothing: at 390px the well is width-limited to a
      24px cell either way, so the height this strip takes was height the board
      could not have used.
    */
    height: 11rem;
  }

  /*
    Co-op has one board, so the strip has no mirror to hold and no minimum
    height to respect: it shrinks to the two lines of text it carries, and
    every pixel it gives up goes to the well both players share.
  */
  .coop-side {
    height: auto;
    align-items: center;
    justify-content: space-between;
    gap: var(--ui-space-2);
  }

  .coop-hint {
    flex: 1 1 auto;
    min-width: 0;
    text-align: right;
  }

  .mini {
    flex: 0 0 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .mini-row {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    gap: var(--ui-space-1);
  }

  .mini-stage {
    width: 7.5rem;
    opacity: 0.9;
    /* A safety net rather than a layout tool: if a future change ever asks
       the renderer for a box smaller than its minimum cell allows, the mirror
       gets cropped instead of drawn on top of the board being played. */
    overflow: hidden;
  }

  .who {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--ui-space-1);
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--ui-ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .who.away {
    color: var(--ui-danger);
  }

  /* --- your board ---------------------------------------------------- */

  .mine {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    display: flex;
    gap: var(--ui-space-1);
    align-items: stretch;
    padding: 0 var(--ui-space-2);
  }

  .stage {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    display: flex;
    align-items: center;
    /* Left, not centred: the canvas is wider than the well (the hold/next rail
       lives in it), so centring would open a gap between the incoming meter
       and the wall the garbage is about to come through. */
    justify-content: flex-start;
  }

  .mine--coop .stage {
    justify-content: center;
  }

  .meter-slot {
    flex: 0 0 auto;
  }

  canvas {
    display: block;
  }

  /* The variant's readout, beside the mini-board on a phone: a name on the
     left and its number on the right, with figures that do not shift. */
  .stats {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 11rem;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 0.3rem;
  }

  .stat {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--ui-space-2);
    min-width: 0;
  }

  .stat dt {
    min-width: 0;
    font: var(--ui-text-eyebrow);
    font-weight: 700;
    color: var(--ui-ink-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stat dd {
    margin: 0;
    font: var(--ui-text-body);
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--ui-ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stat--quiet dd {
    color: var(--ui-ink-2);
  }

  .stat dd.stat-away,
  .stat dd.stat-urgent {
    color: var(--ui-danger);
  }

  .foot {
    margin: var(--ui-space-3) 0 0;
    display: flex;
    justify-content: flex-end;
  }

  /* The lobby, over the boards: the room's whole screen while it waits. */
  .lobby-layer {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    background: var(--ui-ground);
  }

  /* --- desktop: the panel beside your board --------------------------- */

  @media (min-width: 1024px) {
    .side {
      flex-direction: column;
      height: auto;
      padding: 0 0 var(--ui-space-3);
      gap: var(--ui-space-2);
    }

    .mini {
      flex: 1 1 auto;
    }

    /* The opponent's board is drawn as large as the panel allows: nobody is
       holding a desktop in one hand, so it stops needing to be a thumbnail. */
    .mini-stage {
      width: auto;
      height: 22rem;
      flex: 1 1 auto;
    }

    .stats {
      flex: 0 0 auto;
      max-width: none;
    }

    .coop-side {
      flex-direction: row;
    }

    .mine {
      padding: var(--ui-space-3) 0;
    }

    .stage {
      justify-content: center;
    }
  }
</style>
